<template>
  <div
v-if="localItems || localItems.length > 0"
class="tw-mt-10 tw-max-w-2xl tw-mx-auto tw-bg-white tw-shadow-md tw-rounded-lg"
>
    <div
v-for="(item, index) in localItems"
:key="item.id"
class="tw-border-b tw-border-gray-200 last:tw-border-b-0"
>
      <button
          :aria-expanded="item.isOpen"
          :aria-controls="`accordion-content-${item.id}`"
          class="tw-w-full tw-flex tw-items-center tw-justify-between tw-py-4 tw-px-6 tw-text-left tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 focus:tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-indigo-500 focus-visible:tw-ring-opacity-75"
          @click="toggleItem(item.id)"
      >
        <span>{{ item.heading }}</span>
        <svg
            class="tw-w-6 tw-h-6 tw-transform tw-transition-transform tw-duration-300"
            :class="{ 'tw-rotate-180': item.isOpen }"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
          <path
stroke-linecap="round"
stroke-linejoin="round"
stroke-width="2"
d="M19 9l-7 7-7-7"
/>
        </svg>
      </button>
      <div
          :id="`accordion-content-${item.id}`"
          :ref="el => setContentRef(el, index)"
          class="tw-overflow-hidden tw-transition-all tw-duration-500 tw-ease-in-out"
          :style="{ maxHeight: item.isOpen ? item.contentHeight + 'px' : '0px' }"
      >
        <div
class="tw-p-6 tw-text-gray-600"
v-html="item.content"
/>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductSections',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
      validator: (items) => {
        return items.every(item =>
            typeof item.heading === 'string' &&
            typeof item.content === 'string' // Adjust if content can be other types
        );
      }
    },
    allowMultipleOpen: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      localItems: [],
      contentRefs: [], // To store refs to content divs
    };
  },
  watch: {
    items: {
      handler: 'initializeItems',
      deep: true,
      immediate: true, // Initialize when component is created and prop is first passed
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.contentRefs = []; // Clean up refs
  },
  methods: {
    initializeItems() {
      this.contentRefs = []; // Reset refs array
      this.localItems = this.items.map(item => ({
        ...item,
        isOpen: !!item.initiallyOpen,
        contentHeight: 0,
      }));

      this.$nextTick(() => {
        this.localItems.forEach((item, index) => {
          if (item.isOpen) {
            this.calculateContentHeight(index);
          }
        });
      });
    },
    setContentRef(el, index) {
      if (el) {
        this.contentRefs[index] = el;
      }
    },
    toggleItem(itemId) {
      const itemIndex = this.localItems.findIndex(item => item.id === itemId);
      if (itemIndex === -1) return;

      const currentlyOpen = this.localItems[itemIndex].isOpen;

      if (!this.allowMultipleOpen && !currentlyOpen) {
        this.localItems.forEach((item, idx) => {
          if (idx !== itemIndex && item.isOpen) {
            item.isOpen = false;
            // No need to recalculate height here, it will be set to 0px by the style
          }
        });
      }

      this.localItems[itemIndex].isOpen = !currentlyOpen;

      this.$nextTick(() => {
        if (this.localItems[itemIndex].isOpen) {
          this.calculateContentHeight(itemIndex);
        }
        // If closing, max-height is set to 0px directly by the style binding,
        // so explicit height calculation to 0 is not strictly needed here.
        // The contentHeight property will retain its value for when it re-opens.
      });
    },
    calculateContentHeight(index) {
      const contentDiv = this.contentRefs[index];
      if (contentDiv && this.localItems[index] && this.localItems[index].isOpen) {
        this.localItems[index].contentHeight = contentDiv.scrollHeight;
      } else if (contentDiv && this.localItems[index] && !this.localItems[index].isOpen) {
        // This case might not be strictly necessary as 0px is set by style
        // but useful if you wanted to force a calculation before setting to 0.
        // For now, we let the style handle the 0px on close.
      }
    },
    handleResize() {
      this.localItems.forEach((item, index) => {
        if (item.isOpen) {
          // Recalculate height for open items as their content might reflow
          // Temporarily set height to auto to measure, then set it back
          // This can be tricky and cause flickers if not handled carefully.
          // A simpler approach for resize is to ensure scrollHeight is re-read.
          this.$nextTick(() => { // Ensure DOM is stable
            this.calculateContentHeight(index);
          });
        }
      });
    }
  },
};
</script>

<style scoped>
/* Scoped styles can be added here if needed, but Tailwind handles most styling. */
/* The last:tw-border-b-0 utility is from Tailwind v2.1+.
   If using an older version, you might need a different approach or custom CSS.
   Alternatively, you can handle this in the loop structure slightly differently. */
.last\:tw-border-b-0:last-child {
  border-bottom-width: 0;
}
</style>